<template>
    <Layout class="cursus-register mb-5" :ready="ready" :breadcrumb="breadcrumbItems">
        <template v-if="ready">
            <div class="mt-4">
                <div class="row mx-n2 mx-lg-n3">
                    <div class="col-lg-9 px-2 px-lg-3">
                        <div class="cursus-register__header">
                            <h2 class="cursus-register__surtitle text-primary">
                                <template v-if="order && order.is_quote">
                                    demander un devis
                                </template>
                                <template v-else-if="course.registration_level === 'APPLICATION'">
                                    candidater
                                </template>
                                <template v-else>
                                    s’inscrire
                                </template>
                            </h2>
                            <h1 class="content-title mb-3 px-0">
                                <router-link :to="courseRouteUrl($router, course)" custom v-slot="{ navigate, href }">
                                    <a :href="href" @click="navigate">{{ course.title }}</a>
                                </router-link>
                            </h1>
                            <template v-if="session">
<!--                                <h2 class="cursus-register__session-title">-->
<!--                                    {{ session.title }}-->
<!--                                </h2>-->
                                <template v-if="session.description">
                                    <div class="cursus-content cursus-register__description text-primary" v-html="session.description">
                                    </div>
                                </template>
                            </template>
                        </div>
                        <div>
                            <template v-if="canRegister">
                                <router-view
                                    :order.sync="order"
                                    :course="course"
                                    :session="session"
                                    :next-route="nextRoute"
                                    :previous-route="previousRoute"
                                    :loading="loading"
                                    :steps="steps"
                                    :current-step="currentStep"
                                    :order-step="orderStep"
                                    :user="registerInfo && registerInfo.user"
                                    @confirm="handleConfirmClicked"
                                    @error="handleStepError"
                                >
                                </router-view>
                            </template>
                            <template v-if="errorMessage">
                                <div class="alert alert-danger mt-3">
                                    <div style="white-space: pre-line">{{ errorMessage }}</div>
                                </div>
                            </template>
                        </div>
                    </div>
                    <div class="col-md-3 d-none d-lg-block px-2 px-lg-3">
                        <div class="sticky-top" style="top: 10rem; z-index: 0">
                            <CourseItem class="cursus-register__course-item mb-3" :course="course" :show-tag="false">
                                <template v-slot:footer>
                                    <button class="btn btn-link btn-sm p-0" tabindex="-1">
                                        <span class="mr-1">&lt;</span> revenir au programme
                                    </button>
                                </template>
                            </CourseItem>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else-if="errorMessage">
            <div class="alert alert-danger mt-3">
                {{ errorMessage }}
            </div>
            <router-link :to="{ name:'list' }" custom v-slot="{ href, navigate }">
                <a :href="href" @click="navigate">
                    &lt; Revenir à la liste des formations
                </a>
            </router-link>
        </template>
        <template v-else>
            <slot />
        </template>
    </Layout>
</template>

<script>
    import {
        getCourse,
        handleUnauthenticated,
        getOrder,
        postOrderConfirm,
        getSessionRegister,
        postOrderQuote,
    } from "../../api";
    import { sessionRegisterBreadcrumb } from "../../util/breadcrumb";
    import { updateDocumentTitle } from "../../util/title";
    import Icon from "../ui/Icon.vue";
    import Layout from "../Layout.vue";
    import CourseItem from "../CourseItem.vue";
    import Steps from "../ui/Steps.vue";
    import {
        currentStep,
        nextStep,
        previousStep,
        registerSteps,
        stepIndex,
        stepRouteIndex
    } from "../../util/register";
    import { courseRouteUrl } from "../../util/url";

    export default {
        components: {
            Steps,
            CourseItem,
            Icon,
            Layout,
        },
        props: {
            showBreadcrumb: Boolean,
            breadcrumb: Array,
        },
        data() {
            return {
                ready: false,
                order: null,
                course: null,
                registerInfo: null,

                loading: false,
                errorMessage: null,
                canRegister: true,
            }
        },
        watch: {
            '$route': 'handleRouteChanged',
            'order': 'handleOrderChanged',
        },
        computed: {
            session() {
                return this.course?.sessions.find(session => session.id == this.$route.params.sessionId);
            },
            breadcrumbItems() {
                return this.showBreadcrumb
                    ? [...this.breadcrumb, ...sessionRegisterBreadcrumb(this.course, this.session)]
                    : null;
            },
            steps() {
                if(!this.course || !this.session) {
                    return [];
                }
                return registerSteps({
                    order: this.order,
                    course: this.course,
                    session: this.session,
                })
            },
            orderStep() {
                return this.order?.step ?? this.$route.name.replace('register-', '');
            },
            nextStep() {
                return nextStep(this.steps, this.$route, this.orderStep);
            },
            currentStep() {
                return currentStep(this.steps, this.$route);
            },
            previousStep() {
                return previousStep(this.steps, this.$route);
            },
            nextRoute() {
                return this.nextStep?.route;
            },
            previousRoute() {
                return this.previousStep?.route;
            },
        },
        methods: {
            courseRouteUrl,
            async init() {
                const { courseId, sessionId, orderId } = this.$route.params;

                [this.course, this.registerInfo, this.order] = await Promise.all([
                    getCourse({ courseId }),
                    getSessionRegister({ courseId, sessionId, orderId })
                        .catch(error => {
                            if(error.response?.status === 401) {
                                this.errorMessage = `Vous n’êtes plus connecté, essayez de recharger la page`;
                                this.canRegister = false;
                            } else if(error.response?.status === 403) {
                                this.errorMessage = error.response.data.message;
                                this.canRegister = false;
                            } else {
                                return Promise.reject(error);
                            }
                        }),
                    this.getOrder(),
                ])
                .catch(error => {
                    if(this.canRegister) {
                        this.errorMessage = `Cette inscription est invalide ou n’est plus disponible`;
                        this.canRegister = false;
                        return Promise.reject(error);
                    }
                })
                updateDocumentTitle(this.breadcrumbItems);
            },
            getOrder() {
                const { courseId, sessionId, orderId } = this.$route.params;
                const withTotal = this.$route.name === 'register-summary'
                    || this.$route.name === 'register-checkout';

                if(!orderId) {
                    return null;
                }

                return getOrder({ courseId, sessionId, orderId, withTotal })
                    .then(order => {
                        // if order is finally free in checkout page, redirect to summary
                        if(this.$route.name === 'register-checkout' && order.current_total === 0) {
                            this.$router.replace({
                                name: 'register-summary',
                            });
                        }
                        return order;
                    })
                    .catch(error => {
                        if(error.response?.status === 403) {
                            this.errorMessage = error.response.data.message;
                        } else if(error.response?.status === 404) {
                            this.errorMessage = `Cette inscription est invalide ou n’est plus disponible`;
                        }
                        this.canRegister = false;

                        // setTimeout to log at sentry without block rendering
                        setTimeout(() => {
                            throw error;
                        });
                    });
            },
            setOrderStep() {
                if(this.order) {
                    this.order.next_step = this.nextStep?.value ?? null;
                }
            },
            handleOrderChanged() {
                this.setOrderStep();
            },
            handleStepError(errorMessage) {
                this.errorMessage = errorMessage;
            },
            postOrder() {
                const { courseId, sessionId, orderId } = this.$route.params;

                if(this.order.is_quote) {
                    return postOrderQuote({
                        courseId, sessionId, orderId,
                        data: this.order,
                    });
                }

                return postOrderConfirm({
                    courseId, sessionId, orderId,
                    data: this.order,
                });
            },
            async handleConfirmClicked() {
                this.errorMessage = null;
                this.loading = true;

                this.postOrder()
                    .then(data => {
                        if(data.payment_page_url) {
                            location.href = data.payment_page_url;
                        }
                        else {
                            this.$router.push({
                                name: 'register-confirmation',
                                params: {
                                    orderId: this.order.id,
                                },
                                query: {
                                    message: data.message,
                                    account_url: data.account_url,
                                },
                            });
                        }
                    })
                    .catch(handleUnauthenticated)
                    .catch(error => {
                        if(error.response?.status === 422) {
                            const errors = error.response.data.errors;
                            this.errorMessage = Object.values(errors).join('\n');
                        }
                        else if(error.response?.status === 403) {
                            this.errorMessage = `Une erreur est survenue pendant l’inscription : ${error.response.data.message}`;
                        }
                        else {
                            this.errorMessage = `Impossible de s’inscrire à cette session pour le moment, veuillez réessayer plus tard`;
                            return Promise.reject(error);
                        }
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },
            async handleRouteChanged(route, from) {
                this.setOrderStep();

                if(this.ready) {
                    this.errorMessage = null;
                    const index = stepIndex(this.steps, this.orderStep);
                    if(index >= 0 && stepRouteIndex(this.steps, route) > index) {
                        this.$router.replace(this.steps[index].route);
                        return;
                    }
                }

                // update all data when going to summary and checkout
                if(route?.name === 'register-summary') {
                    await this.init();
                }
                else if(route?.name === 'register-checkout') {
                    this.order = await this.getOrder();
                }

                const rect = document.querySelector('#register-step')?.getBoundingClientRect();
                if(rect) {
                    window.scrollBy(0, rect.top - 170);
                }
            },
            async redirectIfNeeded() {
                if(this.$route.name === 'register-confirmation') {
                    return;
                }

                // Draft order was found for the current user
                if(this.$route.name === 'session-register' && this.registerInfo?.current_order_id) {
                    await this.$router.replace({
                        name: 'register-order',
                        params: {
                            orderId: this.registerInfo.current_order_id,
                        },
                    });
                    this.order = await this.getOrder();
                }

                if(this.steps?.length > 0) {
                    if(this.$route.name === 'register-order' && this.order) {
                        const step = this.steps.find(step => step.value === this.orderStep) ?? this.steps[0];
                        await this.$router.replace(step.route);
                    }

                    if(stepRouteIndex(this.steps, this.$route) < 0) {
                        await this.$router.replace(this.steps[0].route);
                    }
                }
            },
        },
        async created() {
            await this.init();
            await this.redirectIfNeeded();
            this.ready = true;
        },
    }
</script>
